/**
 * The organisation's analytics — a port of `bimgo-ai-web`'s
 * `OrgAnalyticsDashboard`. It replaced the org tab's old summary-and-trend
 * pair, which read one endpoint per figure; this reads one.
 *
 * **Two requests, and the first is not optional.** The default call — no range —
 * answers with the current cycle *and* the server's `as_of` date. A preset
 * window is measured back from that date, so the range query cannot even be
 * built until the default has landed. That is the whole reason for the
 * two-query shape rather than one call with parameters, and why the current
 * cycle keeps rendering while a range is still in flight: the two halves of this
 * screen answer different questions and fail independently.
 *
 * **The current-cycle card never changes.** Picking a range moves the second
 * card only — "where the org stands now" is not a function of what an admin is
 * browsing, and re-fetching it on every range change would make it flicker for
 * no new information.
 *
 * `credits` is BIMGO credits (it replaced a `raw_tokens` field, which is why
 * the label key is still `rawTokens`), mapped onto the chart's `tokens` series
 * by `analyticsPoints` so one chart serves both scopes.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import { API_BASE_URL, isConfigured } from "../../lib/config";
import { statusOf } from "../../lib/threads";
import {
  analyticsPoints,
  formatDateRange,
  getOrgUsageDashboard,
  presetRange,
  type OrgUsageDashboard,
  type Range,
  type UsageDateRange,
} from "../../lib/usage";
import { LoadingBlock } from "../settings/Spinner";
import { RangeControls } from "./RangeControls";
import { UsageTrend } from "./UsageTrend";

type Load<T> =
  | { kind: "loading" }
  | { kind: "forbidden" }
  | { kind: "error" }
  | { kind: "loaded"; value: T };

export function OrgAnalytics({ orgId, token }: { orgId: string; token: string | null }) {
  const { t, locale } = useLocale();

  const [preset, setPreset] = useState<Range>(30);
  const [custom, setCustom] = useState<UsageDateRange | null>(null);
  const [base, setBase] = useState<Load<OrgUsageDashboard | null>>({ kind: "loading" });
  const [ranged, setRanged] = useState<Load<OrgUsageDashboard | null>>({ kind: "loading" });

  // Read at request time rather than depended on: the session guard rotates the
  // token on every window focus, and a token dependency re-ran both fetches —
  // skeletons and all — on each refocus.
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const describe = (error: unknown): Load<never> =>
    statusOf(error) === 403 ? { kind: "forbidden" } : { kind: "error" };

  // The default call: the current cycle, and `as_of`.
  useEffect(() => {
    if (!orgId || !isConfigured()) return;
    let cancelled = false;
    setBase({ kind: "loading" });
    getOrgUsageDashboard(tokenRef.current, orgId, null, { apiBaseUrl: API_BASE_URL() })
      .then((value) => !cancelled && setBase({ kind: "loaded", value }))
      .catch((e: unknown) => !cancelled && setBase(describe(e)));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const asOf = base.kind === "loaded" ? (base.value?.as_of?.slice(0, 10) ?? null) : null;
  // A preset is a window ending on the server's day, so it cannot be built
  // before the default call lands — see the header.
  const window = custom ?? (asOf ? presetRange(asOf, preset) : null);

  useEffect(() => {
    if (!orgId || !window || !isConfigured()) return;
    let cancelled = false;
    setRanged({ kind: "loading" });
    getOrgUsageDashboard(tokenRef.current, orgId, window, { apiBaseUrl: API_BASE_URL() })
      .then((value) => !cancelled && setRanged({ kind: "loaded", value }))
      .catch((e: unknown) => !cancelled && setRanged(describe(e)));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, window?.from, window?.to]);

  const selected = ranged.kind === "loaded" ? ranged.value?.selected_range : null;
  const points = useMemo(() => analyticsPoints(selected), [selected]);

  const rangeLabel = window
    ? formatDateRange(window.from, window.to, locale)
    : t(`dashboard.chart.range.${preset}d`);

  return (
    <>
      <div className="settings-card">
        <h3 className="card-title">{t("dashboard.analytics.currentCycle")}</h3>
        {base.kind === "loaded" && base.value ? (
          <p className="settings-note">
            {formatDateRange(base.value.current_cycle.from, base.value.current_cycle.to, locale)}
          </p>
        ) : null}
        <Loaded state={base}>
          {(value) =>
            !value ? (
              <p className="settings-note is-centered">{t("dashboard.analytics.empty")}</p>
            ) : (
              <MetricGrid
                metrics={[
                  {
                    label: t("dashboard.analytics.requests"),
                    value: value.current_cycle.requests,
                    lead: true,
                  },
                  {
                    label: t("dashboard.analytics.rawTokens"),
                    value: value.current_cycle.credits,
                    lead: true,
                  },
                  {
                    label: t("dashboard.analytics.activeMembers"),
                    value: value.current_cycle.active_users,
                  },
                  {
                    label: t("dashboard.analytics.cloudSkillsTotal"),
                    value: value.current_cycle.cloud_skills_total,
                  },
                ]}
              />
            )
          }
        </Loaded>
      </div>

      <div className="settings-card">
        <div className="card-head-row">
          <div>
            <h3 className="card-title">{t("dashboard.analytics.historical")}</h3>
            <p className="settings-note">{rangeLabel}</p>
          </div>
          <RangeControls
            preset={preset}
            custom={custom}
            asOf={asOf}
            onPreset={setPreset}
            onCustom={setCustom}
          />
        </div>

        <Loaded state={ranged}>
          {() =>
            !selected ? (
              <p className="settings-note is-centered">{t("dashboard.analytics.empty")}</p>
            ) : (
              <>
                <MetricGrid
                  metrics={[
                    {
                      label: t("dashboard.analytics.requests"),
                      value: selected.requests,
                      lead: true,
                    },
                    {
                      label: t("dashboard.analytics.rawTokens"),
                      value: selected.credits,
                      lead: true,
                    },
                    {
                      label: t("dashboard.analytics.activeUsers"),
                      value: selected.active_users,
                    },
                    {
                      label: t("dashboard.analytics.cloudSkillsCreated"),
                      value: selected.cloud_skills_created,
                    },
                  ]}
                />
                <h4 className="analytics-subtitle">{t("dashboard.analytics.trends")}</h4>
                <UsageTrend
                  points={points}
                  tokenLabel={t("dashboard.analytics.rawTokens")}
                  emptyLabel={t("dashboard.analytics.empty")}
                />
              </>
            )
          }
        </Loaded>
      </div>
    </>
  );
}

function MetricGrid({
  metrics,
}: {
  metrics: { label: string; value: number; lead?: boolean }[];
}) {
  const { locale } = useLocale();
  const numberLocale = locale === "zh" ? "zh-TW" : "en-US";
  return (
    <div className="stat-row">
      {metrics.map((metric) => (
        <div className="stat" key={metric.label}>
          <div className="stat-label">{metric.label}</div>
          {/* The two headline figures are set larger than the two counts
              beside them, as in the reference: requests and credits are what
              the card is about, members and skills are context. */}
          <div className={metric.lead ? "stat-value" : "stat-value is-secondary"}>
            {Math.round(metric.value ?? 0).toLocaleString(numberLocale)}
          </div>
        </div>
      ))}
    </div>
  );
}

/** A 403 is "not an org admin", not a failure — the session behind it is fine. */
function Loaded<T>({
  state,
  children,
}: {
  state: Load<T>;
  children: (value: T) => React.ReactNode;
}) {
  const { t } = useLocale();
  if (state.kind === "loading") return <LoadingBlock size={24} />;
  if (state.kind === "forbidden") return <p className="settings-note">{t("error.forbidden")}</p>;
  if (state.kind === "error") {
    return (
      <p className="settings-msg err" role="alert">
        {t("dashboard.analytics.loadFailed")}
      </p>
    );
  }
  return <>{children(state.value)}</>;
}
